import type { Paths } from '../../../utils/types';
import { asset } from './asset';
import { autohedgeStatus, AutoHedgingStatus } from './autoHedgeStatus';
import { buyingPower } from './buyingPower';
import {
  active,
  addressRoutingType,
  addressStatus,
  bitmap,
  bps,
  button,
  careOrderStatus,
  connectionStatus,
  cumBaseQty,
  custom,
  email,
  exposureStatus,
  exposureUtilization,
  extendedDuration,
  feeMode,
  filledCounterAmount,
  filledNotional,
  filler,
  formError,
  iconButton,
  json,
  marketColor,
  mode,
  modeWithMessage,
  openNotional,
  percent,
  pricingAggregation,
  processStep,
  productTypeField,
  quoteMarkets,
  quoteSide,
  quoteStatus,
  quoteValue,
  reconMismatchStatus,
  remove,
  revision,
  role,
  strategy,
  textArray,
  totalNotional,
  tradeStatus,
  tradingMarketAccounts,
  transactionType,
  transferStatus,
} from './columns';
import { connectionStatusWithText } from './connectionStatusWithText';
import { countOf } from './countOf';
import { counterparty } from './counterparty';
import { counterpartyFromMarketAccount } from './counterpartyFromMarketAccount';
import { credentialAge } from './credentialAge';
import { creditUsageWarning } from './creditUsageWarning';
import { currency } from './currency';
import { customer } from './customer';
import { date, time } from './date';
import { dateOnly } from './dateOnly';
import { expandRow } from './expandRow';
import { expiryCountdown } from './expiryCountdown';
import { exposureDefinition } from './exposureDefinition';
import { filledPercent } from './filledPercent';
import { fixingIndex } from './fixingIndex';
import { fixingIndexTimeAtDate } from './fixingIndexTimeAtDate';
import { fixingIndices } from './fixingIndices';
import { fixingPrice } from './fixingPrice';
import { delta, gamma, rho, theta, vega } from './greeks';
import { group } from './group';
import { hamburgerMenu } from './hamburgerMenu';
import { id } from './id';
import { indexMark } from './indexMark';
import { market } from './market';
import { account, marketAccount, marketAccountSourceAccountID, marketAccountTransfer } from './marketAccount';
import { marketAccountGroupDerived } from './marketAccountGroupDerived';
import { marketAccountTradingStatus } from './marketAccountTradingStatus';
import { marketAccounts } from './marketAccounts';
import { markets } from './markets';
import { modeStatus } from './modeStatus';
import { number } from './number';
import { optionGroup, optionSide, optionStrike, STRIKE_CLASSNAME, type StrikeOptionColumn } from './options';
import { orderParameters } from './orderParameters';
import { orderStartTime } from './orderStartTime';
import { orderStatus, type OrderStatusParams } from './orderStatus';
import { pmsWarnings } from './pmsWarning';
import { positionDirection } from './positionDirection';
import { positionExpiry } from './positionExpiry';
import { positionThresholdMeter } from './positionThresholdMeter';
import { positionUnderlyingAsset } from './positionUnderlyingAsset';
import { predictionCategory, predictionEvent, predictionExpiry, predictionSeries } from './predictionMarket';
import { prettyText } from './prettyText';
import { price } from './price';
import { productType } from './productType';
import { quoteMarket } from './quoteMarket';
import { reconCheckpointEvalStatus } from './reconCheckpointEvalStatus';
import { reconCheckpointStatus } from './reconCheckpointStatus';
import { reconMatchStatus } from './reconMatchStatus';
import { reconWarning } from './reconWarning';
import { rowCount } from './rowCount';
import { security } from './security';
import { customerSecurityMarketData, securityMarketData } from './securityMarketData';
import { securityNumber } from './securityNumber';
import { securityText } from './securityText';
import { side } from './side';
import { size } from './size';
import { sizedAvg } from './sizedAvg';
import { spread } from './spread';
import { strikeFwdPrice } from './strikeFwdPrice';
import { stringArray } from './stringArray';
import { subAccount } from './subAccount';
import { subAccountName } from './subAccountName';
import { text } from './text';
import { textLiveEdit } from './textLiveEdit';
import { thresholdAsset } from './thresholdAsset';
import { thresholdType } from './thresholdType';
import { toggle } from './toggle';
import { transferDescription } from './transferDescription';
import { user } from './user';
import { warning } from './warning';
import { windowDuration } from './windowDuration';

export * from './autoGroupSortingDefaults';
export * from './baseColumn';
export { baseColumn } from './baseColumn';
export { getOrderStatusColor, type FilledPercentColumnParams, type FilledPercentColumnValue } from './filledPercent';
export { getAgGridColId } from './getAgGridColId';
export { indexMark } from './indexMark';

export { asset } from './asset';
export type { AssetColumnProps } from './asset';
export { createGetAssetColumnQuickFilterText, getDisplayedAssetDescription } from './assetQuickFilterText';
export {
  PRODUCT_TYPES_WITH_BLOTTER_SECURITY_DESCRIPTION,
  shouldShowBlotterSecurityDescription,
} from './blotterSecurityDescriptionPolicy';
export { getColorBarColumn, type ColorBarProps } from './colorBar';
export type { QuoteStatusColumnParams } from './columns';
export type { CountOfParams, CountOfValue } from './countOf';
export * from './credentialAge';
export { currency } from './currency';
export type { DateParams, DateTimezoneParam } from './date';
export { expiryCountdown } from './expiryCountdown';
export type { ExpiryCountdownParams, ExpiryCountdownValue } from './expiryCountdown';
export type { GreekColumnParams } from './greeks';
export * from './hamburgerMenu';
export { getDisplayIdFromValue } from './id';
export type { MarketsColumnParams } from './markets';
export type { ModeStatusColumnParams } from './modeStatus';
export { number } from './number';
export type { NumberColumnParams } from './number';
export type { PositionExpiryValue } from './positionExpiry';
export {
  getPredictionCategoryTab,
  getPredictionEventForSecurity,
  PREDICTION_CATEGORY_OTHER,
  resolvePredictionEventSeries,
  resolvePredictionMarketFields,
} from './predictionMarket';
export type {
  PredictionMarketColumnParams,
  PredictionMarketLookup,
  PredictionMarketResolvedFields,
} from './predictionMarket';
export type { PriceParams } from './price.types';
export type { ProductTypeColumnParams } from './productType';
export type { QuoteMarketColumnParams } from './quoteMarket';
export { resolvePositionUnderlying } from './resolvePositionUnderlying';
export type { SecurityColumnParams } from './security';
export {
  createGetSecurityIdentifierColumnQuickFilterText,
  createGetSecuritySymbolColumnQuickFilterText,
  getSecurityIdentifierColumns,
  getSecurityIdentifierMetadataKeys,
  getSecurityIdentifierSearchText,
  getSecuritySymbolSearchText,
  wrapSecurityIdentifierQuickFilterText,
} from './securityIdentifiers';
export { getSecurityMarketDataCellValue, SecurityMarketDataCell, SecurityMarketDataHeader } from './securityMarketData';
export type {
  CustomerMarketDataRequestParams,
  CustomerMarketDataRequestParamsContext,
  CustomerSecurityMarketDataColumnParams,
  CustomerSecurityMarketDataRequestParams,
  SecurityMarketDataCellPriceActions,
  SecurityMarketDataCellValue,
  SecurityMarketDataColumnParams,
  SecurityMarketDataGetPriceActionsContext,
  SecurityMarketDataRequestParams,
  SecurityMarketDataRequestParamsContext,
} from './securityMarketData';
export { side } from './side';
export type { SizeColumnParams } from './size.types';
export type { SpreadColumnParams } from './spread';
export type { StringArrayColumnParams, StringArrayColumnValue } from './stringArray';
export { subAccount } from './subAccount';
export type { SubAccountColumnParams } from './subAccount';
export type { SubAccountAutocompleteItem, SubAccountNameColumnParams } from './subAccountName';
export type { TextColumnParams } from './text';
export type { TextLiveEditColumnParams } from './textLiveEdit';
export type { ToggleParams } from './toggle.types';
export { baseTreeGroupColumnDef } from './treeGroup';
export * from './types';
export type { ColDefFactory, Column } from './types';
export {
  amountObjectComparator,
  displayNameComparator,
  numericColumnComparator,
  stringColumnComparator,
  stringColumnComparatorMultiColumn,
} from './utils';
export { AutoHedgingStatus, STRIKE_CLASSNAME };
export type { OrderStatusParams, StrikeOptionColumn };
export const columnTypes = {
  active,
  addressStatus,
  asset,
  bps,
  button,
  bitmap,
  cumBaseQty,
  connectionStatus,
  connectionStatusWithText,
  countOf,
  counterparty,
  counterpartyFromMarketAccount,
  currency,
  custom,
  customer,
  credentialAge,
  creditUsageWarning,
  role,
  date,
  dateOnly,
  delta,
  email,
  expandRow,
  expiryCountdown,
  exposureDefinition,
  exposureStatus,
  exposureUtilization,
  filledCounterAmount,
  filledNotional,
  filledPercent,
  filler,
  fixingIndex,
  fixingIndexTimeAtDate,
  formError,
  gamma,
  group,
  hamburgerMenu,
  iconButton,
  id,
  indexMark,
  json,
  market,
  markets,
  account,
  marketAccount,
  marketAccountGroupDerived,
  marketAccountSourceAccountID,
  marketAccountTradingStatus,
  marketAccountTransfer,
  spread,
  fixingPrice,
  marketAccounts,
  marketColor,
  mode,
  modeStatus,
  modeWithMessage,
  number,
  openNotional,
  orderParameters,
  orderStartTime,
  orderStatus,
  percent,
  pmsWarnings,
  buyingPower,
  positionDirection,
  positionExpiry,
  positionUnderlyingAsset,
  predictionCategory,
  predictionEvent,
  predictionExpiry,
  predictionSeries,
  price,
  pricingAggregation,
  quoteMarket,
  fixingIndices,
  feeMode,
  processStep,
  productType,
  productTypeField,
  quoteMarkets,
  quoteSide,
  quoteStatus,
  quoteValue,
  reconCheckpointStatus,
  reconCheckpointEvalStatus,
  reconMatchStatus,
  reconMismatchStatus,
  reconWarning,
  remove,
  revision,
  rho,
  rowCount,
  security,
  securityText,
  securityNumber,
  customerSecurityMarketData,
  securityMarketData,
  side,
  size,
  sizedAvg,
  strategy,
  stringArray,
  subAccount,
  subAccountName,
  positionThresholdMeter,
  prettyText,
  text,
  textLiveEdit,
  textArray,
  theta,
  thresholdAsset,
  thresholdType,
  time,
  toggle,
  totalNotional,
  tradeStatus,
  transferDescription,
  tradingMarketAccounts,
  transferStatus,
  autohedgeStatus,
  transactionType,
  addressRoutingType,
  user,
  vega,
  warning,
  windowDuration,
  optionStrike,
  optionSide,
  optionGroup,
  strikeFwdPrice,
  careOrderStatus,
  extendedDuration,
} as const;

export type ColumnType = keyof typeof columnTypes;

declare module './types' {
  export interface Column {
    /** Column type */
    type: keyof typeof columnTypes;
  }

  export type ColumnDefByColumnType<TData> = {
    [K in keyof typeof columnTypes]: Omit<Parameters<(typeof columnTypes)[K]>[0], 'type' | 'field'> &
      ({ type: K; field: Paths<TData> & string } | { type: K; id: string });
  };

  /** Column definition type to map params to Talos Column Definitions */
  export type ColumnDef<TData> = ColumnDefByColumnType<TData>[keyof typeof columnTypes];
}
